import { lazy, Suspense, type ComponentType } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfirmDialogHost } from '@/components/ui/ConfirmDialog';
import { ToastViewport } from '@/components/ui/Toast';
import { Spinner } from '@/components/ui/States';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth, RequireStaff } from '@/app/RequireAuth';
import { LoginPage } from '@/pages/LoginPage';
import { HomePage } from '@/pages/HomePage';
import { ReviewPage } from '@/pages/ReviewPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { CompanyLayout } from '@/pages/company/CompanyLayout';
import { CompanyOverviewPage } from '@/pages/company/CompanyOverviewPage';
import { CompanyActivityTab } from '@/pages/company/CompanyActivityTab';
import { CompanyDeliverablesTab } from '@/pages/company/CompanyDeliverablesTab';
import { CompanyPeopleTab } from '@/pages/company/CompanyPeopleTab';
import { CompanyKnowledgeTab } from '@/pages/company/CompanyKnowledgeTab';
import { WorkspaceLayout } from '@/pages/workspace/WorkspaceLayout';
import { WorkspaceOverviewTab } from '@/pages/workspace/WorkspaceOverviewTab';
import { WorkspaceActivityTab } from '@/pages/workspace/WorkspaceActivityTab';
import { WorkspaceDeliverablesTab } from '@/pages/workspace/WorkspaceDeliverablesTab';
import { WorkspaceContextTab } from '@/pages/workspace/WorkspaceContextTab';
import { WorkspaceKnowledgeTab } from '@/pages/workspace/WorkspaceKnowledgeTab';
import { ProjectLayout } from '@/pages/project/ProjectLayout';
import { ProjectKnowledgeTab } from '@/pages/project/ProjectKnowledgeTab';
import { ProjectRunsTab } from '@/pages/project/ProjectRunsTab';
import { RunDetailPage } from '@/pages/project/RunDetailPage';
import { ProjectDeliverablesTab } from '@/pages/project/ProjectDeliverablesTab';
import { ProjectReviewTab } from '@/pages/project/ProjectReviewTab';
import { PortalLayout } from '@/pages/portal/PortalLayout';
import { PortalHivePage } from '@/pages/portal/PortalHivePage';
import { PortalDepartmentPage } from '@/pages/portal/PortalDepartmentPage';
import { PortalDeliverablesPage } from '@/pages/portal/PortalDeliverablesPage';
import { PortalReportsPage } from '@/pages/portal/PortalReportsPage';
import { PortalConnectPage } from '@/pages/portal/PortalConnectPage';

// Route-split the staff-only and markdown-heavy pages so a client's initial
// bundle doesn't carry pages they can't reach (Operations, Agents, Platform,
// settings) or the markdown/deliverable renderer until it's actually opened.
const lazyPage = <K extends string>(load: () => Promise<Record<K, ComponentType>>, key: K) =>
  lazy(() => load().then((m) => ({ default: m[key] })));

const ClaudeSettingsPage = lazyPage(() => import('@/pages/settings/ClaudeSettingsPage'), 'ClaudeSettingsPage');
const CompanySettingsPage = lazyPage(() => import('@/pages/company/CompanySettingsPage'), 'CompanySettingsPage');
const WorkspaceConnectorsTab = lazyPage(() => import('@/pages/workspace/WorkspaceConnectorsTab'), 'WorkspaceConnectorsTab');
const WorkspaceSettingsTab = lazyPage(() => import('@/pages/workspace/WorkspaceSettingsTab'), 'WorkspaceSettingsTab');
const DeliverableViewerPage = lazyPage(() => import('@/pages/project/DeliverableViewerPage'), 'DeliverableViewerPage');
const ProjectSettingsTab = lazyPage(() => import('@/pages/project/ProjectSettingsTab'), 'ProjectSettingsTab');
const AgentsPage = lazyPage(() => import('@/pages/agents/AgentsPage'), 'AgentsPage');
const AgentEditorPage = lazyPage(() => import('@/pages/agents/AgentEditorPage'), 'AgentEditorPage');
const OperationsConnectorsPage = lazyPage(() => import('@/pages/operations/OperationsConnectorsPage'), 'OperationsConnectorsPage');
const OperationsWorkflowsPage = lazyPage(() => import('@/pages/operations/OperationsWorkflowsPage'), 'OperationsWorkflowsPage');
const OperationsUsersPage = lazyPage(() => import('@/pages/operations/OperationsUsersPage'), 'OperationsUsersPage');

// IA: Company → Workspace (engagement) → Project (workstream), all slug-based
// in the URL — no ambient tenant state. One shell, role-scoped nav: STAFF see
// everything; CLIENTS are scoped server-side to their companies.

export function App() {
  return (
    <BrowserRouter>
      <ConfirmDialogHost />
      <ToastViewport />
      <Suspense fallback={<div className="p-8"><Spinner label="Loading…" /></div>}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* The client portal — the customer-facing surface, its own shell. */}
        <Route
          path="/portal"
          element={
            <RequireAuth>
              <PortalLayout />
            </RequireAuth>
          }
        >
          <Route index element={<PortalHivePage />} />
          <Route path="d/:companySlug" element={<PortalDepartmentPage />} />
          <Route path="deliverables" element={<PortalDeliverablesPage />} />
          <Route path="reports" element={<PortalReportsPage />} />
          <Route path="connect" element={<PortalConnectPage />} />
        </Route>

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/" element={<HomePage />} />
          <Route path="/review" element={<ReviewPage />} />

          <Route path="/c/:companySlug" element={<CompanyLayout />}>
            <Route index element={<CompanyOverviewPage />} />
            <Route path="activity" element={<CompanyActivityTab />} />
            <Route path="deliverables" element={<CompanyDeliverablesTab />} />
            <Route path="people" element={<CompanyPeopleTab />} />
            <Route path="knowledge" element={<CompanyKnowledgeTab />} />
            <Route
              path="settings"
              element={
                <RequireStaff>
                  <CompanySettingsPage />
                </RequireStaff>
              }
            />
            <Route path="w/:workspaceSlug" element={<WorkspaceLayout />}>
              <Route index element={<WorkspaceOverviewTab />} />
              <Route path="activity" element={<WorkspaceActivityTab />} />
              <Route path="deliverables" element={<WorkspaceDeliverablesTab />} />
              <Route path="context" element={<WorkspaceContextTab />} />
              <Route path="knowledge" element={<WorkspaceKnowledgeTab />} />
              <Route
                path="connectors"
                element={
                  <RequireStaff>
                    <WorkspaceConnectorsTab />
                  </RequireStaff>
                }
              />
              <Route
                path="settings"
                element={
                  <RequireStaff>
                    <WorkspaceSettingsTab />
                  </RequireStaff>
                }
              />
              <Route path="p/:projectSlug" element={<ProjectLayout />}>
                <Route index element={<Navigate to="runs" replace />} />
                <Route path="runs" element={<ProjectRunsTab />} />
                <Route path="runs/:runId" element={<RunDetailPage />} />
                <Route path="deliverables" element={<ProjectDeliverablesTab />} />
                <Route path="deliverables/:deliverableId" element={<DeliverableViewerPage />} />
                <Route path="review" element={<ProjectReviewTab />} />
                <Route path="knowledge" element={<ProjectKnowledgeTab />} />
                <Route
                  path="settings"
                  element={
                    <RequireStaff>
                      <ProjectSettingsTab />
                    </RequireStaff>
                  }
                />
              </Route>
            </Route>
          </Route>

          <Route
            path="/connectors"
            element={
              <RequireStaff>
                <OperationsConnectorsPage />
              </RequireStaff>
            }
          />
          <Route
            path="/workflows"
            element={
              <RequireStaff>
                <OperationsWorkflowsPage />
              </RequireStaff>
            }
          />
          <Route
            path="/users"
            element={
              <RequireStaff>
                <OperationsUsersPage />
              </RequireStaff>
            }
          />
          <Route
            path="/agents"
            element={
              <RequireStaff>
                <AgentsPage />
              </RequireStaff>
            }
          />
          <Route
            path="/agents/new"
            element={
              <RequireStaff>
                <AgentEditorPage />
              </RequireStaff>
            }
          />
          <Route
            path="/agents/:id"
            element={
              <RequireStaff>
                <AgentEditorPage />
              </RequireStaff>
            }
          />

          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/settings/claude"
            element={
              <RequireStaff>
                <ClaudeSettingsPage />
              </RequireStaff>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
